/**
 * Dashboard version selector — controls whether Home renders the legacy
 * line-graph dashboard or the new Penpot-matched dashboard.
 *
 * Behavior:
 *   - The toggle is available to every company in every environment, so anyone
 *     can move to the new dashboard and back again.
 *   - Which view opens first still depends on the environment: prod starts on
 *     the legacy dashboard, everywhere else starts on the new one. A user's own
 *     choice is remembered in localStorage and wins over that default.
 *
 * Once the new dashboard is the only one, delete this file and revert Home to
 * `return <Dashboard />`.
 */

import { useCallback, useState } from "react";

const STORAGE_KEY = "eserv:dashboard-version";

export type DashboardVersion = "old" | "new";

function isProdEnv(): boolean {
  const url = process.env.REACT_APP_SERVER_URL || "";
  // Match api.eserv.com exactly — excludes api-uat / api-qa / api-dev.
  return /^https?:\/\/api\.eserv\.com(?:[/:]|$)/.test(url);
}

export function useDashboardVersion() {
  const defaultVersion: DashboardVersion = isProdEnv() ? "old" : "new";

  const [version, setVersionState] = useState<DashboardVersion>(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored === "new" || stored === "old" ? stored : defaultVersion;
    } catch {
      return defaultVersion;
    }
  });

  const setVersion = useCallback((next: DashboardVersion) => {
    setVersionState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* localStorage unavailable — keep in-memory only */
    }
  }, []);

  return { version, setVersion };
}
